import { apiClient } from "../client";
import { ApiErrorHandler } from "../utils/error-handler";
import { Process } from "../../../types/process";
import { PaginatedResponse } from "../types/common";

/**
 * Endpoints de processos
 * 
 * Funcionalidades:
 * - CRUD de processos
 * - Busca e filtros
 * - Relacionamentos com documentos
 */

export interface CreateProcessRequest {
  code: string;
  description: string;
  modality_id: string | number; // Em vez de "process_type"
  object: string;
  estimated_value: string | number; // Em vez de "budget"
  publication_date: string;
  responsible: string;
  opening_date?: string | null; // Em vez de "deadline"
  opening_time?: string | null;
  status?: string;
}

export interface UpdateProcessRequest {
  id: string;
  data: Partial<Process>;
}

export const processApi = {
  /**
   * Lista todos os processos
   */
  list: async (): Promise<PaginatedResponse<Process>> => {
    try {
      const response = await apiClient.get("/processes/");
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.list");
    }
  },

  /**
   * Busca um processo pelo ID
   */
  get: async (id: string): Promise<Process> => {
    try {
      const response = await apiClient.get(`/processes/${id}/`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.get");
    }
  },

  /**
   * Cria um novo processo
   */
  create: async (data: CreateProcessRequest): Promise<Process> => {
    try {
      const response = await apiClient.post("/processes/", data);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.create");
    }
  },

  /**
   * Atualiza um processo
   */
  update: async (id: string, data: Partial<Process>): Promise<Process> => {
    try {
      const response = await apiClient.put(`/processes/${id}/`, data);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.update");
    }
  },

  /**
   * Remove um processo
   */
  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/processes/${id}/`);
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.delete");
    }
  },

  /**
   * Busca processos por tipo
   */
  getByType: async (type: string): Promise<PaginatedResponse<Process>> => {
    try {
      const response = await apiClient.get(`/processes/?process_type=${type}`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.getByType");
    }
  },

  /**
   * Busca processos por agência
   */
  getByAgency: async (agency: string): Promise<PaginatedResponse<Process>> => {
    try {
      const response = await apiClient.get(`/processes/?agency=${agency}`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.getByAgency");
    }
  },

  /**
   * Busca processos por ano
   */
  getByYear: async (year: number): Promise<PaginatedResponse<Process>> => {
    try {
      const response = await apiClient.get(`/processes/?year=${year}`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ProcessAPI.getByYear");
    }
  },
};

/**
 * Endpoints de modalidades
 */
export const modalityApi = {
  /**
   * Lista todas as modalidades
   */
  list: () => apiClient.get("/modalities/"),

  /**
   * Busca uma modalidade pelo ID
   * 
   * @param id - ID da modalidade
   */
  get: (id: string) => apiClient.get(`/modalities/${id}/`),

  /**
   * Cria uma nova modalidade
   * 
   * @param data - Dados da modalidade
   */
  create: (data: any) => apiClient.post("/modalities/", data),

  /**
   * Atualiza uma modalidade
   * 
   * @param id - ID da modalidade
   * @param data - Dados para atualização
   */
  update: (id: string, data: any) => apiClient.put(`/modalities/${id}/`, data),

  /**
   * Remove uma modalidade
   * 
   * @param id - ID da modalidade
   */
  delete: (id: string) => apiClient.delete(`/modalities/${id}/`),
}; 